import { Identifiable, Reference } from '@approvalmax/types';
import { domain } from 'modules/data';
import { useDispatch } from 'modules/react-redux';
import { ItemForEdit } from 'pages/requestForm/components';
import { useCallback, useMemo, useState } from 'react';

import {
    changeXeroLineItemAllChecked,
    changeXeroLineItemsAccount,
    changeXeroLineItemsItem,
    changeXeroLineItemsTax,
    changeXeroLineItemsTrackingCategoryValue,
    cloneXeroLineItems,
    cloneXeroLineItemsAll,
    removeXeroLineItems,
    removeXeroLineItemsAll,
} from '../../../actions';
import { XeroLineItemDropdownCell } from '../XeroLineItemDropdownCell/XeroLineItemDropdownCell';
import { XeroLineItemItemCell } from '../XeroLineItemItemCell/XeroLineItemItemCell';
import { filterTaxCodesByAccountClass } from './XeroLineItemsSection.helpers';
import { messages } from './XeroLineItemsSection.messages';
import { UseGroupEditItemsParams } from './XeroLineItemsSection.types';

export const useGroupEditItems = ({
    contact,
    xeroContext,
    itemVisible,
    taxRateVisible,
    isEditorMode,
    lineItems,
    getAccountReadonly,
    getTrackingCategoryReadonly,
}: UseGroupEditItemsParams) => {
    const dispatch = useDispatch();

    const [isGroupEditPopupVisible, setIsGroupEditPopupVisible] = useState(false);
    const selectedItems = useMemo(() => lineItems.filter((el) => el.checked), [lineItems]);

    const handleGroupRemove = useCallback(() => {
        if (selectedItems.length === lineItems.length) {
            dispatch(removeXeroLineItemsAll());
        } else {
            dispatch(removeXeroLineItems(selectedItems.map((lineItem) => lineItem.id)));
        }
    }, [dispatch, lineItems.length, selectedItems]);

    const handleGroupClone = useCallback(() => {
        if (selectedItems.length === lineItems.length) {
            dispatch(cloneXeroLineItemsAll());
        } else {
            dispatch(cloneXeroLineItems(selectedItems.map((item) => item.id)));
        }

        dispatch(changeXeroLineItemAllChecked(false));
    }, [dispatch, lineItems.length, selectedItems]);

    const openGroupEdit = useCallback(() => {
        setIsGroupEditPopupVisible(true);
    }, []);

    const closeGroupEdit = useCallback(() => {
        setIsGroupEditPopupVisible(false);
        dispatch(changeXeroLineItemAllChecked(false));
    }, [dispatch]);

    const groupItems = useMemo(() => {
        const res: ItemForEdit<Identifiable>[] = [];

        if (itemVisible) {
            res.push({
                id: 'item',
                name: messages.colNameItem,
                render: ({ value, onChange }) => (
                    <XeroLineItemItemCell
                        value={value as domain.XeroItem}
                        onChange={onChange}
                        placeholder={messages.cellPlaceholderItem}
                    />
                ),
                onSave: (newItem: domain.XeroItem) => {
                    dispatch(
                        changeXeroLineItemsItem(
                            selectedItems.map((lineItem) => lineItem.id),
                            newItem
                        )
                    );
                },
            });
        }

        const accountVisible = selectedItems.every((item) => !getAccountReadonly(item));

        if (accountVisible) {
            res.push({
                id: 'account',
                name: messages.colNameAccount,
                onSave: (newAccount: domain.XeroAccount) => {
                    dispatch(
                        changeXeroLineItemsAccount({
                            lineItemIds: selectedItems.map((lineItem) => lineItem.id),
                            newAccount,
                            recalculateTax: !isEditorMode,
                        })
                    );
                },
                prepareChange: (value, values) => {
                    const res: Record<string, Identifiable | null> = {
                        ...values,
                        account: value,
                    };

                    if (values.taxRate) {
                        const taxCodes = filterTaxCodesByAccountClass(
                            xeroContext.taxCodes,
                            value as domain.XeroAccount
                        );

                        if (!taxCodes.includes(values.taxRate as domain.XeroTaxCode)) {
                            delete res.taxRate;
                        }
                    }

                    return res;
                },
                render: ({ value, onChange }) => (
                    <XeroLineItemDropdownCell
                        items={xeroContext.accounts}
                        value={value as domain.XeroAccount}
                        onChange={onChange}
                        defaultValueOnFocus={contact?.purchase?.account}
                        placeholder={messages.cellPlaceholderAccount}
                    />
                ),
            });
        }

        if (taxRateVisible) {
            res.push({
                id: 'taxRate',
                name: messages.colNameTaxRate,
                onSave: (newTax: domain.XeroTaxCode) => {
                    dispatch(
                        changeXeroLineItemsTax(
                            selectedItems.map((lineItem) => lineItem.id),
                            newTax
                        )
                    );
                },
                render: ({ value, onChange, values }) => {
                    const taxCodes = filterTaxCodesByAccountClass(
                        xeroContext.taxCodes,
                        values.account as domain.XeroAccount | undefined
                    );

                    return (
                        <XeroLineItemDropdownCell
                            items={taxCodes}
                            value={value as domain.XeroTaxCode}
                            onChange={onChange}
                            placeholder={messages.cellPlaceholderTaxRate}
                        />
                    );
                },
            });
        }

        xeroContext.trackingCategories.forEach((tc) => {
            if (!getTrackingCategoryReadonly(tc.category.id)) {
                res.push({
                    id: tc.category.id,
                    name: tc.category.text,
                    onSave: (newValue: Reference) => {
                        dispatch(
                            changeXeroLineItemsTrackingCategoryValue(
                                selectedItems.map((lineItem) => lineItem.id),
                                tc.category.id,
                                newValue
                            )
                        );
                    },
                    render: ({ value, onChange }) => (
                        <XeroLineItemDropdownCell
                            items={tc.options}
                            value={value || null}
                            onChange={onChange}
                            defaultValueOnFocus={
                                contact?.purchase?.trackingCategories.find((x) => x.category.id === tc.category.id)
                                    ?.value
                            }
                            placeholder={messages.cellPlaceholderAccount}
                        />
                    ),
                });
            }
        });

        return res;
    }, [
        itemVisible,
        selectedItems,
        taxRateVisible,
        xeroContext.trackingCategories,
        xeroContext.taxCodes,
        xeroContext.accounts,
        dispatch,
        getAccountReadonly,
        isEditorMode,
        contact?.purchase?.account,
        contact?.purchase?.trackingCategories,
        getTrackingCategoryReadonly,
    ]);

    const isGroupRemoveVisible = useMemo(() => selectedItems.every((item) => !item.hideRemove), [selectedItems]);
    const isGroupCloneVisible = useMemo(() => selectedItems.every((item) => !item.hideClone), [selectedItems]);
    const isGroupEditVisible = groupItems.length > 0;
    const isShowGroupButtons =
        selectedItems.length > 0 && (isGroupRemoveVisible || isGroupCloneVisible || isGroupEditVisible);

    return {
        groupItems,
        isShowGroupButtons,
        handleGroupRemove,
        handleGroupClone,
        openGroupEdit,
        closeGroupEdit,
        isGroupEditVisible,
        isGroupRemoveVisible,
        isGroupCloneVisible,
        isGroupEditPopupVisible,
    };
};
